/**
 * WidgetCard: one overview widget on a Carbon Tile, with its own states so one failing call never blanks the page.
 * Shows, in order: the `h2` title with optional `headerExtra` (a lag Tag, an "as of" time), then a skeleton while
 * `isLoading`, an error notification with Retry when `isError`, an EmptyState when `isEmpty`, else the children.
 *
 * Props: `title`, `headerExtra`, `isLoading`, `isError`, `error` (the thrown value, for the message), `onRetry`,
 * `isEmpty`, `emptyHeading`, `emptyDescription`, `children`.
 */
import { ActionableNotification, SkeletonText, Tile } from '@carbon/react';
import { useTranslation } from '@pos/i18n';
import type { ReactNode } from 'react';
import { EmptyState } from '../components/EmptyState';
import { asProblem, problemMessage } from '../problems';
import './overview.scss';

export interface WidgetCardProps {
  title: ReactNode;
  headerExtra?: ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  error?: unknown;
  onRetry?(): void;
  isEmpty?: boolean;
  emptyHeading?: ReactNode;
  emptyDescription?: ReactNode;
  children?: ReactNode;
}

export function WidgetCard({
  title,
  headerExtra,
  isLoading = false,
  isError = false,
  error,
  onRetry,
  isEmpty = false,
  emptyHeading,
  emptyDescription,
  children,
}: WidgetCardProps) {
  const { t } = useTranslation();
  let body: ReactNode;
  if (isLoading) {
    body = <SkeletonText paragraph lineCount={3} />;
  } else if (isError) {
    body = (
      <ActionableNotification
        inline
        kind="error"
        role="alert"
        lowContrast
        hideCloseButton
        title={problemMessage(t, asProblem(error))}
        actionButtonLabel={t('admin.retry')}
        onActionButtonClick={() => onRetry?.()}
      />
    );
  } else if (isEmpty) {
    body = (
      <EmptyState headingLevel={3} heading={emptyHeading ?? t('admin.overview.empty')} description={emptyDescription} />
    );
  } else {
    body = children;
  }
  return (
    <Tile className="pos-widget-card">
      <div className="pos-widget-card__header">
        <h2 className="pos-widget-card__title">{title}</h2>
        {headerExtra && <div className="pos-widget-card__extra">{headerExtra}</div>}
      </div>
      {body}
    </Tile>
  );
}
